import { apiClient } from './client';
import { queryClient } from './queryClient';
import { clearSessionHint, hasSessionHint } from '@/auth/sessionHint';
import { clearOnboardingHint } from '@/auth/onboardingHint';

/**
 * 401 interceptor for the typed API client.
 *
 * When any authenticated request against `/api/v1/*` returns 401 the
 * server is telling us the session is gone (cookie expired, revoked on
 * another tab, server-side cleanup). Per
 * apps/client/docs/overview.md §"Auth surface", the client's single
 * auth side effect is: clear the localStorage hint, clear the React
 * Query cache, and bounce the user to /sign-in.
 *
 * Implemented as an openapi-fetch middleware. The middleware runs on
 * every response and inspects the status code; on 401 it triggers the
 * side effect once and lets the original response continue back to
 * the caller. The caller's React Query / openapi-fetch error path
 * still runs as normal — components don't need to special-case 401.
 *
 * Called once at module load by main.tsx (side-effect import).
 *
 * Guard: only react if the hint says we *thought* we were signed in.
 * A 401 from a public-page request (which shouldn't happen for /api/v1
 * paths today, but might in the future) wouldn't have a hint to clear
 * and shouldn't redirect a logged-out user back to /sign-in twice.
 */
/**
 * Default redirect — full-page navigation via window.location.assign.
 * A hard reload is robust here (no stale React tree survives the
 * transition) and the cost is negligible on an auth-expired flow that
 * already implies a context switch. Tests pass a stub to capture the
 * call without bouncing jsdom around; jsdom's `location.assign` is
 * non-configurable, so injecting via parameter is the only clean way
 * to override it.
 */
const defaultRedirect = (url: string): void => {
  window.location.assign(url);
};

/**
 * Build the onResponse handler that the middleware uses. Exported as a
 * factory (rather than baked into installAuthInterceptor) so tests can
 * invoke the handler with a crafted Response object directly, without
 * routing the call through openapi-fetch's URL-resolution layer (which
 * trips on the SPA's relative baseUrl `/api/v1` under jsdom).
 */
export function createAuth401Handler(
  redirect: (url: string) => void = defaultRedirect,
): (ctx: { response: Response }) => Response {
  return ({ response }) => {
    if (response.status === 401 && hasSessionHint()) {
      clearSessionHint();
      // Tear down any in-flight onboarding state too — the session is
      // gone, so the onboarding gate keyed off the same account is no
      // longer meaningful. See apps/client/src/auth/onboarding-hint.ts.
      clearOnboardingHint();
      // Drop the React Query cache so a re-mount after sign-in starts
      // clean. queryClient is a module singleton
      // (src/api/queryClient.ts).
      queryClient.clear();
      redirect('/sign-in');
    }
    return response;
  };
}

export function installAuthInterceptor(redirect: (url: string) => void = defaultRedirect): void {
  apiClient.use({ onResponse: createAuth401Handler(redirect) });
}
